import { skipToken } from '@apollo/client/react'
import { memo, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'
import type { ValueOf } from 'type-fest'
import { ArtworkType } from 'src/services/apollo/generated/graphql'
import { useListLocalizedTacks } from 'src/services/apollo/tack'
import { cx } from 'src/utils/cx'
import { track } from 'src/utils/tracker'
import { useContestConfig } from '../Contest/useContestConfig'
import { getContestExtra } from '../Contest/utils'
import { Preloader } from '../Preloader/Preloader'
import { TagChipGroup } from './TagChip'
import type { DetailedTag, TagItem } from './utils'
import { getTagCategory, getTagName, TagCategory } from './utils'

export type TagRecommendationProps = {
  // Tags
  selectedTags: TagItem[]
  onTagSelected: (tag: TagItem) => void
  onTagRemove: (tag: TagItem) => void
  onTagRecommendationLoaded?: (tags: TagItem[]) => void

  // Challenge
  challenge?: number
  onChallengeChange: (challenge?: number) => void

  // Recommendations
  mediaId?: string
  isVideoTask?: boolean
}

const TagRecommendationComponent = ({
  // Tags
  selectedTags,
  onTagSelected,
  onTagRemove,
  onTagRecommendationLoaded,

  // Challenge
  challenge,
  onChallengeChange,

  // Recommendations
  mediaId,
  isVideoTask,
}: TagRecommendationProps) => {
  const { t } = useTranslation(['common'])
  const {
    tags: recommendedTags,
    loading,
    error,
  } = useRecommendedTags({
    mediaId: mediaId,
    isVideoTask: isVideoTask,
    limit: 30,
  })

  useEffect(() => {
    const categoryTags = recommendedTags.find(
      t => t.category === TagCategory.Category,
    )
    if (categoryTags) {
      onTagRecommendationLoaded?.(categoryTags.data.map(t => t.tag))
    }
  }, [onTagRecommendationLoaded, recommendedTags])

  const isSelected = useCallback(
    (category: string, tag: DetailedTag) => {
      if (category === TagCategory.Contest && tag.contest?.contestId) {
        return challenge === tag.contest.contestId
      }

      return selectedTags.some(selectedTag => {
        return getTagName(selectedTag) === tag.name
      })
    },
    [challenge, selectedTags],
  )

  const handleTagClicked = useCallback(
    (category: string, type: 'add' | 'remove', tag: DetailedTag) => {
      track('add_tag', {
        name: tag.name,
        challenge: challenge,
        recommendType: category,
        selected: type === 'add',
      })

      if (type === 'add') {
        onTagSelected(tag.tag)
      } else {
        onTagRemove(tag.tag)
      }

      if (category === TagCategory.Contest && tag.contest?.contestId) {
        const challengeValue = match(type)
          .with('add', () => tag.contest?.contestId)
          .with('remove', () => undefined)
          .exhaustive()
        onChallengeChange(challengeValue)
      }
    },
    [challenge, onChallengeChange, onTagRemove, onTagSelected],
  )

  const tagChipGroupProps = useMemo(() => {
    return recommendedTags.map(group => ({
      category: group.category,
      tags: group.data.map(t => {
        const selected = isSelected(group.category, t)
        return {
          type: (selected ? 'remove' : 'add') as 'add' | 'remove',
          tag: t,
          onAdd: () => handleTagClicked(group.category, 'add', t),
          onRemove: () => handleTagClicked(group.category, 'remove', t),
          variant: 'recommendation' as 'recommendation' | 'selection',
          category: group.category,
          disabled: selected, // disable selected tags in tag selected area
        }
      }),
    }))
  }, [recommendedTags, isSelected, handleTagClicked])

  if (loading) {
    return (
      <div className="flex flex-col flex-wrap justify-center gap-2 p-4 rounded-md relative">
        <Preloader />
      </div>
    )
  }

  if (error) {
    console.error(`failed to recommend tags by media: ${mediaId}`, error)
    return null
  }

  return (
    <div
      className={cx(
        'flex flex-col flex-wrap justify-center gap-2 p-4 rounded-md relative',
      )}
    >
      <h4 className="text-gray-400 pb-1 absolute top-2 right-2">
        {t('common:common.suggested')}
      </h4>

      {tagChipGroupProps.map(group => {
        if (group.category === TagCategory.Other) return null
        if (group.tags.length === 0) return null

        let label: string = group.category
        if (group.category === 'category') {
          label = t('common:category.label')
        }
        if (group.category === 'feature') {
          label = t('common:common.feature')
        }

        return (
          <div key={group.category} className="inline-flex flex-col gap-2">
            <div className="text-gray-700 dark:text-gray-300 uppercase text-sm tracking-wider">
              {label}
            </div>
            <TagChipGroup chips={group.tags} size="medium" foldLimit={10} />
          </div>
        )
      })}
    </div>
  )
}

export const TagRecommendation = memo(TagRecommendationComponent)

export type useRecommendedTagsInput = {
  mediaId?: string
  isVideoTask?: boolean
  limit?: number
}

export type CategorizedTags = {
  category: ValueOf<typeof TagCategory>
  data: DetailedTag[]
}

export const useRecommendedTags = ({
  mediaId,
  isVideoTask,
  limit = 30,
}: useRecommendedTagsInput) => {
  const contestConfig = useContestConfig()
  const {
    data: allTags = [],
    loading,
    error,
  } = useListLocalizedTacks(mediaId ? { first: limit, mediaId } : skipToken)

  // Get Contest
  const activeContests = useMemo(() => {
    return contestConfig.filter(c => {
      if (!c.isInProgress) return false // Contest not in progress
      const artworkType = getContestExtra(c)?.artworkType

      // If the artwork type is animated and it's not a video task, skip it
      if (artworkType === ArtworkType.AnimatedArtwork && !isVideoTask) {
        return false
      }

      return true
    })
  }, [contestConfig, isVideoTask])

  // Remove contest tags from recommendations
  const recommendedTags = useMemo(() => {
    return (
      allTags.filter(tag =>
        activeContests.every(contest => contest.name !== tag.name),
      ) || []
    )
  }, [allTags, activeContests])

  // Categorize tags
  const categorizedTags = useMemo(() => {
    const commonTags = recommendedTags.map(tag => ({
      category: getTagCategory(tag),
      tag: tag,
      name: getTagName(tag),
      contest: undefined,
    }))

    // Group tags by category
    const tagsByCategory = commonTags.reduce(
      (acc, tag) => {
        if (!acc[tag.category]) {
          acc[tag.category] = []
        }
        acc[tag.category].push(tag)
        return acc
      },
      {} as Record<string, typeof commonTags>,
    )

    // Convert to CategorizedTags format
    const groupedCommonTags = Object.entries(
      tagsByCategory,
    ).map<CategorizedTags>(([category, tags]) => ({
      category: category as ValueOf<typeof TagCategory>,
      data: tags.map(tag => ({
        tag: tag.tag,
        name: tag.name,
        contest: tag.contest,
      })),
    }))

    const contestTags = {
      category: TagCategory.Contest,
      data: activeContests.map(contest => ({
        tag: {
          id: contest.id,
          name: contest.name,
          mediaId: contest.mediaId,
          extra: {
            recommendType: 'contest',
          },
        } satisfies TagItem,
        name: contest.name,
        contest: {
          contestId: contest.cid,
          lightAccentColor: getContestExtra(contest)?.lightAccentColor,
          darkAccentColor: getContestExtra(contest)?.darkAccentColor,
        },
      })),
    } as CategorizedTags

    return [...groupedCommonTags, contestTags]
  }, [activeContests, recommendedTags])

  // Order categories
  // Contest -> Category -> Feature -> Other
  const order = [
    TagCategory.Contest,
    TagCategory.Category,
    TagCategory.Feature,
    TagCategory.Other,
  ]
  categorizedTags.sort((a, b) => {
    return order.indexOf(a.category) - order.indexOf(b.category)
  })

  return {
    tags: categorizedTags,
    loading: loading,
    error: error,
  }
}
